import type {
  MapGeometry,
  MapRouting,
  MapRoutingBarrier,
} from "../shared/types";

export type RoutingPoint = [number, number];
export type RoutingView = { zoom: number; centerX: number; centerY: number };
export type RoutingSelection = {
  kind: "area" | "barrier" | "door";
  id: string;
};

export function clampRoutingPoint(
  point: RoutingPoint,
  floor: MapGeometry["floor"],
): RoutingPoint {
  return [
    Math.max(floor.x, Math.min(floor.x + floor.width, point[0])),
    Math.max(floor.y, Math.min(floor.y + floor.height, point[1])),
  ];
}

export function constrainRoutingView(
  view: RoutingView,
  width: number,
  height: number,
): RoutingView {
  const zoom = Math.max(1, Math.min(12, view.zoom));
  return {
    zoom,
    centerX: Math.max(
      width / (2 * zoom),
      Math.min(width - width / (2 * zoom), view.centerX),
    ),
    centerY: Math.max(
      height / (2 * zoom),
      Math.min(height - height / (2 * zoom), view.centerY),
    ),
  };
}

export function fitRoutingView(geometry: MapGeometry): RoutingView {
  const { width, height, floor } = geometry;
  return constrainRoutingView(
    {
      zoom: Math.min(
        width / (floor.width * 1.08),
        height / (floor.height * 1.08),
      ),
      centerX: floor.x + floor.width / 2,
      centerY: floor.y + floor.height / 2,
    },
    width,
    height,
  );
}

export function routingSourcePoint(
  point: RoutingPoint,
  view: RoutingView,
  width: number,
  height: number,
): RoutingPoint {
  return [
    view.centerX + (point[0] - width / 2) / view.zoom,
    view.centerY + (point[1] - height / 2) / view.zoom,
  ];
}

export function barrierPoint(
  barrier: MapRoutingBarrier,
  fraction: number,
): RoutingPoint {
  return [
    barrier.start[0] + (barrier.end[0] - barrier.start[0]) * fraction,
    barrier.start[1] + (barrier.end[1] - barrier.start[1]) * fraction,
  ];
}

export function projectOntoBarrier(
  point: RoutingPoint,
  barrier: MapRoutingBarrier,
) {
  const dx = barrier.end[0] - barrier.start[0];
  const dy = barrier.end[1] - barrier.start[1];
  const lengthSquared = dx * dx + dy * dy;
  const fraction = lengthSquared
    ? Math.max(
        0,
        Math.min(
          1,
          ((point[0] - barrier.start[0]) * dx +
            (point[1] - barrier.start[1]) * dy) /
            lengthSquared,
        ),
      )
    : 0;
  const projected = barrierPoint(barrier, fraction);
  return {
    fraction,
    point: projected,
    distance: Math.hypot(point[0] - projected[0], point[1] - projected[1]),
  };
}

export function removeRoutingConstraint(
  routing: MapRouting,
  selection: RoutingSelection,
): MapRouting {
  return {
    walkableAreas: routing.walkableAreas.filter(
      (item) => selection.kind !== "area" || item.id !== selection.id,
    ),
    barriers: routing.barriers.filter(
      (item) => selection.kind !== "barrier" || item.id !== selection.id,
    ),
    doors: routing.doors.filter(
      (item) =>
        (selection.kind !== "door" || item.id !== selection.id) &&
        (selection.kind !== "barrier" || item.barrierId !== selection.id),
    ),
  };
}

export function routingSelectionVertices(
  routing: MapRouting,
  selection: RoutingSelection | null,
  ring = 0,
): RoutingPoint[] {
  if (!selection) return [];
  if (selection.kind === "area")
    return (routing.walkableAreas.find((area) => area.id === selection.id)
      ?.polygon[ring] ?? []) as RoutingPoint[];
  if (selection.kind === "barrier") {
    const barrier = routing.barriers.find((item) => item.id === selection.id);
    return barrier ? [barrier.start, barrier.end] : [];
  }
  const door = routing.doors.find((item) => item.id === selection.id);
  const barrier = routing.barriers.find((item) => item.id === door?.barrierId);
  return door && barrier
    ? [barrierPoint(barrier, door.start), barrierPoint(barrier, door.end)]
    : [];
}

export function moveRoutingVertex(
  routing: MapRouting,
  selection: RoutingSelection,
  ring: number,
  index: number,
  point: RoutingPoint,
): MapRouting {
  if (selection.kind === "area")
    return {
      ...routing,
      walkableAreas: routing.walkableAreas.map((area) =>
        area.id !== selection.id
          ? area
          : {
              ...area,
              polygon: area.polygon.map((points, ringIndex) =>
                ringIndex !== ring
                  ? points
                  : points.map((item, pointIndex) =>
                      pointIndex !== index ? item : point,
                    ),
              ),
            },
      ),
    };
  if (selection.kind === "barrier")
    return {
      ...routing,
      barriers: routing.barriers.map((barrier) =>
        barrier.id !== selection.id
          ? barrier
          : { ...barrier, [index === 0 ? "start" : "end"]: point },
      ),
    };
  const door = routing.doors.find((item) => item.id === selection.id);
  const barrier = routing.barriers.find((item) => item.id === door?.barrierId);
  if (!door || !barrier) return routing;
  const fraction = projectOntoBarrier(point, barrier).fraction;
  return {
    ...routing,
    doors: routing.doors.map((item) =>
      item.id !== selection.id
        ? item
        : { ...item, [index === 0 ? "start" : "end"]: fraction },
    ),
  };
}

export function routingEditorError(
  error: unknown,
  routing: MapRouting,
): string {
  const message =
    error instanceof Error
      ? error.message
      : "Check the routing shapes before applying them.";
  const parts = message.match(
    /^map\.geometry\.routing(?:\.(walkableAreas|barriers|doors)(?:\[(\d+)\])?)?[^:]*: (.+)$/,
  );
  if (!parts) return message;
  const collection = parts[1] as keyof MapRouting | undefined;
  const index = parts[2] === undefined ? undefined : Number(parts[2]);
  const item =
    collection && index !== undefined ? routing[collection][index] : undefined;
  const label =
    item?.name ||
    (collection === "walkableAreas"
      ? "Walkable areas"
      : collection === "barriers"
        ? "Barriers"
        : collection === "doors"
          ? "Doorways"
          : "Routing");
  const reason = parts[3]
    .replace(
      "must be simple, without intersecting or touching edges",
      "the boundary must not cross or touch itself",
    )
    .replace(
      "must have a start fraction smaller than its end fraction",
      "the start edge must be before the end edge",
    )
    .replace(
      "must not overlap another door on the same barrier",
      "this opening overlaps another doorway on the same barrier",
    )
    .replace(
      "must have distinct start and end points",
      "choose two different endpoints",
    )
    .replace("must have nonzero area", "the boundary must enclose an area");
  return `${label}: ${reason.charAt(0).toUpperCase()}${reason.slice(1)}`;
}
